import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import Input from "../../../components/Input";
import Select from "../../../components/Select";
import Button from "../../../components/Button";
import messages from "../../../assets/locale/messages";
import { NAME_REGEX, ONE_SPACE_ONLY } from "../../../utils/Patterns";
import { setPageMainTitle } from "../../../utils/Helpers";
import {
  addAdminRequest,
  editAdminRequest,
  getAdminDetailsRequest,
  getAdminDetailsResponse,
} from "../../../store/Admins/actions";
import "./AddEditAdmin.scss";

const AddEditAdmin = () => {
  const lang = useSelector((state) => state.locale.lang);
  const adminDetails = useSelector((state) => state.admins.adminDetails);
  const { shared, admins, pagesTitles } = messages[lang];
  const { id } = useParams();
  const dispatch = useDispatch();

  const [adminFormEditable, setAdminFormEditable] = useState(true);

  const adminsRolesList = [
    { id: 0, label: admins.list.superAdmin },
    { id: 1, label: admins.list.admin },
  ];

  useEffect(() => {
    setPageMainTitle("adminsManagement");
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getAdminDetailsRequest(id));
    }
    return () => {
      dispatch(getAdminDetailsResponse({}));
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (id && adminDetails) {
      setFieldTouched({
        username: true,
        email: true,
        role: true,
      });
      setValues({
        username: adminDetails.username,
        email: adminDetails.email,
        role: adminDetails.is_super_admin
          ? adminsRolesList[0]
          : adminsRolesList[1],
      });

      setAdminFormEditable(id && adminDetails.is_super_admin ? false : true);
    }
  }, [adminDetails, id]);

  useEffect(() => {
    if (values["role"]) {
      setFieldValue(
        "role",
        adminsRolesList.find((role) => role.id === values["role"].id)
      );
    }
  }, [lang]);

  const {
    values,
    setFieldValue,
    setValues,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: {
      username: "",
      email: "",
      role: null,
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .required("nameRequired")
        .matches(NAME_REGEX, "nameNotFollowREGEX"),
      email: Yup.string().email("validEmailFormat").required("emailRequired"),
      role: Yup.object().nullable().required("roleRequired"),
    }),
    onSubmit: ({ username, email, role }) => {
      !id
        ? dispatch(
            addAdminRequest({
              username: username.trim(),
              email: email.trim(),
              is_super_admin: role.id == 0,
            })
          )
        : dispatch(
            editAdminRequest({
              id: +id,
              data: {
                username,
                is_super_admin: role.id == 0,
              },
            })
          );
    },
  });

  return (
    <div className="add-edit-admin-page">
      <div className="d-flex mb-4">
        <Typography
          variant="h4"
          sx={{ flexGrow: 1 }}
          className={`page-title ${lang === "en" ? "me-5" : "ms-5"}`}>
          {id ? pagesTitles.adminsEdit : pagesTitles.adminsAdd}
        </Typography>
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="px-2 px-md-5 bg-white rounded-4">
          <div className="py-4">
            <div className="row mt-4">
              <div className="col-12 col-md-6">
                <Input
                  id="username"
                  label={admins.addAdmin.labels.username}
                  name="username"
                  type="text"
                  placeholder={admins.addAdmin.placeholders.username}
                  required
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("username");
                      setFieldValue("username", value);
                    }
                  }}
                  value={values["username"]}
                  inputWrapperClass="mb-4"
                  labelClassName="font-medium mb-2 text-primary"
                  isInputHasErr={!!(touched["username"] && errors["username"])}
                  errMsg={errors["username"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  id="email"
                  label={admins.addAdmin.labels.email}
                  name="email"
                  type="email"
                  disabled={values["email"] != "" && id != undefined}
                  placeholder={admins.addAdmin.placeholders.email}
                  required
                  onChange={(value) => {
                    setFieldTouched("email");
                    setFieldValue("email", value);
                  }}
                  value={values["email"]}
                  inputWrapperClass="mb-4"
                  labelClassName="font-medium mb-2 text-primary"
                  isInputHasErr={!!(touched["email"] && errors["email"])}
                  errMsg={errors["email"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Select
                  name="role"
                  labelClassName="font-medium mb-2 text-primary"
                  label={admins.addAdmin.labels.role}
                  options={adminsRolesList}
                  inputClassName="input-wrapper"
                  placeholder={admins.addAdmin.placeholders.role}
                  required
                  disabled={
                    values["role"]?.id == 0 &&
                    id != undefined &&
                    !adminFormEditable
                  }
                  value={values["role"]}
                  onChange={(value) => {
                    setFieldTouched("role");
                    setFieldValue("role", value);
                  }}
                  hasError={!!(touched["role"] && errors["role"])}
                  errMsg={errors["role"]}
                />
              </div>
            </div>
            <div className="row mt-4 py-4 d-flex justify-content-center">
              <div className="col-4">
                <Button
                  onClick={() => {}}
                  type="submit"
                  block
                  label={shared.save}
                  className="btn-secondary"
                  labelClass="fsize-16 text-white py-1 fweight-600"
                  disabled={!dirty || !isValid}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditAdmin;
